'use client';

import { css, cx } from '@/styled-system/css';
import ListingSearch from '@/src/components/ListingSearch';
import type { AreaResponse } from '@/src/lib/api';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { searchWrapper } from '../../_shell/searchListingSearch.styles';
import { usePathname } from '@/src/i18n/navigation';
import { useSrpViewMode } from './ViewModeContext';
import { SrpSaveSearchButton } from './SrpSaveSearchButton';

type Props = {
  currentAreaFallback?: AreaResponse | null;
  variant?: 'sticky' | 'floating' | 'header';
};

const stickyWrapper = css({
  background: 'bg.neutral',
  paddingTop: 'tighter',
  paddingBottom: 'tighter',
  paddingInline: 'default',
  position: 'sticky',
  top: 0,
  zIndex: 'zIndexStep',
  lg: {
    display: 'none',
  },
});

const floatingWrapper = css({
  width: '100%',
  background: 'transparent',
  position: 'static',
});

const headerWrapper = css({
  display: { base: 'none', lg: 'block' },
  width: '100%',
  // eslint-disable-next-line hotpads/no-magic-numbers -- Figma's desktop search bar is 824px wide
  maxWidth: '51.5em',
  minWidth: 0,
});

const headerSearchWrapper = css({
  width: '100%',
  boxSizing: 'border-box',
  paddingBlock: 'tighter',
  paddingInline: 'default',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  borderStyle: 'solid',
  borderColor: 'border.action.neutral.default',
  borderWidth: { base: 'default', _dark: 'zero' },
});

const feedModeStickyWrapper = css({
  '&[data-search-bar-variant="sticky"][data-srp-feed-mode="true"]': {
    position: 'static',
    lg: {
      display: 'none',
    },
  },
});

function isHdpPath(pathname: string) {
  const finalSegment = pathname.split('/').filter(Boolean).at(-1);
  return finalSegment === 'pad' || finalSegment === 'building';
}

export function SrpListingSearchBar({ currentAreaFallback, variant = 'sticky' }: Props) {
  const pathname = usePathname();
  const { viewMode } = useSrpViewMode();
  const isDesktop = useIsDesktop();
  const isFeedMode = viewMode === 'feed' && !isDesktop;
  const wrapperClass = variant === 'floating' ? floatingWrapper : variant === 'header' ? headerWrapper : stickyWrapper;
  const searchSurfaceClass = variant === 'header' ? headerSearchWrapper : searchWrapper;

  if (variant === 'header' && isHdpPath(pathname)) {
    return null;
  }

  return (
    <div
      className={cx(wrapperClass, feedModeStickyWrapper)}
      data-search-bar-variant={variant}
      data-srp-feed-mode={isFeedMode ? 'true' : 'false'}
      data-srp-component="search-bar"
    >
      <div className={searchSurfaceClass} data-srp-component="search-bar-surface">
        <ListingSearch
          size="sm"
          nearMeArea={currentAreaFallback}
          compactControls={variant !== 'header'}
          trailingAction={<SrpSaveSearchButton />}
        />
      </div>
    </div>
  );
}
